import { PlusOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader, Spin } from 'antd';
import React from 'react';
import PageHeaderTitle from '../../../components/page-header';
import { DrawerWidth } from '../../../config/dimensions';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { ActionOption, DrawerState, ModuleName } from '../../../core/models/enum';
import { OfficialVmWithRoles } from '../../../core/models/OfficialVmWithRoles';
import { isUserAllowed } from '../../../helpers/CheckPermissionHelper';
import { groupMatchOfficialsByOfficialId } from '../../../helpers/CompareHelper';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import { getNewRoute, getSubPageInitialProperties } from '../../../helpers/RoutingHelper';
import {
    MatchDetailVm,
    MatchesClient,
    MatchOfficialsClient,
    MatchOfficialVm,
} from '../../../utils/api';
import MatchOfficialForm from '../../officials/match-official-form';
import OfficialTable from '../../officials/official-table';
import { Props } from './index';
import MatchDetails from './match-details';

interface State {
    matchDetails?: MatchDetailVm;
    isEditMode: boolean;
    id?: string;
    drawerState: DrawerState;
    loading: boolean;
    selectedMatchOfficial?: MatchOfficialVm;
    isUserAllowedToCreateOfficial: boolean;
}

class MatchPage extends React.PureComponent<Props, State> {
    private officialTableRef: any;

    public constructor(props: Props) {
        super(props);

        const {
            location: { pathname },
        } = window;
        this.officialTableRef = React.createRef();

        const initialProperties = getSubPageInitialProperties(
            pathname,
            'matches',
            props.match,
            Routes.ROUTE_MATCHES_READ,
            Routes.ROUTE_MATCHES_EDIT
        );

        this.state = {
            isEditMode: initialProperties.isInitialEditMode,
            id: initialProperties.initialEntityId,
            drawerState:
                initialProperties.activeTabKey &&
                initialProperties.activeTabKey !== Routes.ROUTE_MATCHES_READ &&
                initialProperties.isInitialEditMode
                    ? DrawerState.Edit
                    : DrawerState.Closed,
            loading: true,
            isUserAllowedToCreateOfficial: false,
        };
    }

    public componentDidMount = (): void => {
        this.getPermissions();
        this.getMatchDetails();
    };

    public componentDidUpdate = (prevProps: Props): void => {
        const {
            match: { path },
        } = this.props;

        if (path !== prevProps.match.path) {
            this.updateStateAfterRouteChanges();
        }
    };

    private getPermissions = (): void => {
        const { userProfile } = this.props;

        this.setState({
            isUserAllowedToCreateOfficial: isUserAllowed(
                userProfile,
                ModuleName.Officials,
                ActionOption.Create
            ),
        });
    };

    private updateStateAfterRouteChanges = (): void => {
        const { match } = this.props;
        const {
            location: { pathname },
        } = window;

        const initialProperties = getSubPageInitialProperties(
            pathname,
            'matches',
            match,
            Routes.ROUTE_MATCHES_READ,
            Routes.ROUTE_MATCHES_EDIT
        );

        this.setNewSelectedMatchOfficial();

        this.setState({
            id: initialProperties.initialEntityId,
            isEditMode: initialProperties.isInitialEditMode,
            drawerState:
                initialProperties.activeTabKey &&
                initialProperties.activeTabKey !== Routes.ROUTE_FEDERATIONS_READ &&
                initialProperties.isInitialEditMode
                    ? DrawerState.Edit
                    : DrawerState.Closed,
        });
    };

    private setNewSelectedMatchOfficial = (): void => {
        const { match } = this.props;
        const { matchDetails, selectedMatchOfficial } = this.state;

        const newSelectedMatchOfficial = match.params.officialId
            ? matchDetails?.officials?.find(
                  (fo: MatchOfficialVm): boolean =>
                      fo.officialId.toString() === match.params.officialId
              )
            : selectedMatchOfficial;

        this.setState({
            selectedMatchOfficial: newSelectedMatchOfficial,
        });
    };

    private getMatchDetails = async (section?: string): Promise<void> => {
        const {
            match: {
                params: { matchId },
            },
        } = this.props;

        this.setState({
            loading: true,
        });

        if (matchId) {
            const matchClient = new MatchesClient();
            const matchDetails = await matchClient.getById(parseInt(matchId, 10));

            this.setState({
                matchDetails,
            });
        }

        if (section === 'officials' && this.officialTableRef && this.officialTableRef.current) {
            this.officialTableRef.current.getOfficials();
        }

        this.setNewSelectedMatchOfficial();

        this.setState({
            loading: false,
        });
    };

    private handleDrawerMode = (drawerState: DrawerState, section?: string): void => {
        const {
            history,
            match: {
                params: { matchId },
            },
        } = this.props;

        this.setState({
            drawerState,
        });

        if (section === 'officials') {
            history.push(
                getNewRoute(
                    Routes.ROUTE_MATCH_OFFICIALS,
                    Routes.ROUTE_MATCH_OFFICIALS,
                    Routes.ROUTE_MATCH_OFFICIALS_CREATE,
                    drawerState,
                    ReplaceStrings.MATCH_ID,
                    matchId
                )
            );
        }
    };

    private handleMatchOfficialDelete = async (id?: number): Promise<void> => {
        const { matchDetails } = this.state;

        if (!id) return;

        try {
            const matchOfficials = matchDetails?.officials || [];
            const matchOfficial = matchOfficials.find(
                (mo: MatchOfficialVm): boolean => mo.officialId === id
            );

            if (!matchOfficial?.id) return;

            const client = new MatchOfficialsClient();
            const result = await client.delete(matchOfficial.id);

            if (!result) return;
            success('Match official successfully deleted.');

            await this.getMatchDetails();
            if (this.officialTableRef && this.officialTableRef.current) {
                this.officialTableRef.current.getOfficials();
            }
        } catch (error) {
            errorWithUserMessage('Error deleting data.');
        }
    };

    private isOfficialsPath = (): boolean => {
        const p = this.props.location.pathname;
        return /\/matches\/[^/]+\/officials/.test(p);
    };

    public render(): React.ReactElement {
        const { userProfile, ...routeProps } = this.props;
        const {
            matchDetails,
            isEditMode,
            loading,
            isUserAllowedToCreateOfficial,
            selectedMatchOfficial,
            drawerState,
        } = this.state;

        const showOfficials = this.isOfficialsPath();

        return (
            <Spin spinning={loading}>
                <PageHeader
                    title={
                        <PageHeaderTitle
                            headerTitle={
                                matchDetails
                                    ? `${matchDetails.homeTeamDisplayName} - ${matchDetails.awayTeamDisplayName}`
                                    : ''
                            }
                            tableName="match"
                        />
                    }
                    style={{ padding: '16px 0px' }}
                />
                <div className="tab-padding">
                    <div
                        style={{
                            overflow: 'hidden',
                            width: '100%',
                            transition: 'width .3s cubic-bezier',
                        }}
                    >
                        {matchDetails && showOfficials && (
                            <div>
                                <PageHeader
                                    title=""
                                    extra={
                                        isUserAllowedToCreateOfficial
                                            ? [
                                                  <Button
                                                      key="1"
                                                      type="primary"
                                                      style={{
                                                          zIndex: 10,
                                                          float: 'right',
                                                      }}
                                                      onClick={(): void =>
                                                          this.handleDrawerMode(
                                                              DrawerState.Edit,
                                                              'officials'
                                                          )
                                                      }
                                                  >
                                                      <PlusOutlined className="button-icon-display" />
                                                      Add official
                                                  </Button>,
                                              ]
                                            : []
                                    }
                                    style={{ padding: '16px 0px' }}
                                />
                                {matchDetails.officials && matchDetails.officials.length > 0 ? (
                                    <OfficialTable
                                        userProfile={userProfile}
                                        officialsFromProps={
                                            groupMatchOfficialsByOfficialId(
                                                matchDetails.officials
                                            ) as OfficialVmWithRoles[]
                                        }
                                        wrappedComponentRef={this.officialTableRef}
                                        shouldDisplayRolesColumn
                                        onDelete={this.handleMatchOfficialDelete}
                                        matchId={matchDetails.id}
                                    />
                                ) : (
                                    <div>There are no officials in this federation.</div>
                                )}
                            </div>
                        )}
                        {matchDetails && !showOfficials && (
                            <MatchDetails
                                userProfile={userProfile}
                                isInitialEdit={isEditMode}
                                matchFromProps={matchDetails}
                                refreshAfterSave={this.getMatchDetails}
                            />
                        )}
                    </div>
                    <Drawer
                        title={selectedMatchOfficial ? 'Edit official' : 'Add official'}
                        visible={!!(drawerState && matchDetails) && showOfficials}
                        onClose={(): void => this.handleDrawerMode(DrawerState.Closed, 'officials')}
                        width={DrawerWidth}
                        destroyOnClose
                    >
                        {showOfficials &&
                            matchDetails?.id &&
                            matchDetails?.federationId &&
                            !loading && (
                                <MatchOfficialForm
                                    {...routeProps}
                                    handleClose={(): void => {
                                        this.handleDrawerMode(DrawerState.Closed, 'officials');
                                        this.setState({
                                            selectedMatchOfficial: undefined,
                                        });
                                    }}
                                    refreshAfterSave={(): Promise<void> =>
                                        this.getMatchDetails('officials')
                                    }
                                    existingOfficials={matchDetails?.officials || []}
                                    matchId={matchDetails.id}
                                    federationId={matchDetails.federationId}
                                    matchOfficial={selectedMatchOfficial}
                                />
                            )}
                    </Drawer>
                </div>
            </Spin>
        );
    }
}

export default MatchPage;
